// pages/department/index.tsx
import {
  createDepartmentColumns,
  DepartmentActionType,
} from "@/colDef/departmentColDef";
import {
  Button,
  Card,
  CardContent,
  CustomAlertDialog,
  DataTable,
  ErrorMessage,
  PageContainer,
  Pagination,
  Spinner,
} from "@/components";
import DepartmentDialog from "@/components/formDialog/DepartmentDialog";
import Search from "@/components/Search";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useDeleteDepartmentMutation,
  useDepartmentsQuery,
} from "@/hooks/api/department.queries";
import useDebounce from "@/hooks/useDebounce";
import { useAlertDialog, useFormDialog } from "@/hooks/useDialogState";
import { usePagination } from "@/hooks/usePagination";
import { useAuthStore } from "@/store";
import {
  DepartmentParamsType,
  DepartmentType,
} from "@/services/department.service";
import { Filter, Plus } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

const DepartmentPage = () => {
  const user = useAuthStore((state) => state.user);

  const canCreate = user?.permissions?.includes("department.create");
  const canUpdate = user?.permissions?.includes("department.update");
  const canDelete = user?.permissions?.includes("department.delete");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive"
  >("all");

  const { page, limit, setPage, resetPage } = usePagination({
    initialLimit: 10,
  });
  const debouncedSearch = useDebounce(searchTerm, 400);

  const searchParams = new URLSearchParams(window.location.search);
  const editId = searchParams.get("edit");

  const deleteDialog = useAlertDialog();
  const deptDialog = useFormDialog({
    mode: "create" as "create" | "edit",
    deptId: "",
    deptData: null as DepartmentType | null,
  });

  // Auto-open if navigating here from StaffDialog (inline create)
  useEffect(() => {
    const draftStr = sessionStorage.getItem("inline_create_draft");
    if (draftStr) {
      try {
        const draft = JSON.parse(draftStr);
        if (draft.targetField === "department_id" && draft.isPendingReturn) {
          // Add a tiny delay to ensure proper rendering state
          setTimeout(() => {
            deptDialog.open({ mode: "create", deptId: "", deptData: null });
          }, 100);
        }
      } catch (e) {
        console.error("Failed to parse inline_create_draft", e);
      }
    }
  }, []);

  const filters: DepartmentParamsType = useMemo(
    () => ({
      limit,
      page,
      ...(debouncedSearch && { search: debouncedSearch }),
      ...(statusFilter !== "all" && { status: statusFilter }),
    }),
    [debouncedSearch, statusFilter, page, limit]
  );

  const { data, isLoading, isError, refetch } = useDepartmentsQuery(filters);

  // Auto-open edit dialog if URL has edit param
  useMemo(() => {
    if (editId && data?.departments) {
      const deptToEdit = data.departments.find(
        (d: DepartmentType) => d.id === editId
      );
      if (deptToEdit) {
        deptDialog.open({
          mode: "edit",
          deptId: editId,
          deptData: deptToEdit,
        });
        // Clear param without reload
        const newUrl = window.location.pathname;
        window.history.replaceState({ path: newUrl }, "", newUrl);
      }
    }
  }, [editId, data?.departments]);
  const deleteMutation = useDeleteDepartmentMutation(filters);

  const departments = data?.departments ?? [];
  const pagination = data?.metaData?.pagination;
  const totalEntries = pagination?.total ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleDelete = useCallback(() => {
    deleteMutation.mutate(deleteDialog.state.mantraId, {
      onSuccess: () => deleteDialog.close(),
    });
  }, [deleteMutation, deleteDialog]);

  const handleActionClick = useCallback(
    (payload: DepartmentActionType) => {
      if (payload.action === "delete") {
        deleteDialog.open({
          message: (
            <span>
              Are you sure you want to delete{" "}
              <strong className="text-gray-800">{payload.name}</strong>? This
              action cannot be undone.
            </span>
          ),
          mantraId: payload.id,
          mantraTitle: payload.name,
          action: "delete",
        });
      } else if (payload.action === "edit") {
        const deptData =
          departments.find((d: DepartmentType) => d.id === payload.id) ?? null;
        deptDialog.open({ mode: "edit", deptId: payload.id, deptData });
      }
    },
    [deleteDialog, deptDialog, departments]
  );

  const columns = useMemo(
    () =>
      createDepartmentColumns(handleActionClick, {
        canUpdate: !!canUpdate,
        canDelete: !!canDelete,
      }),
    [handleActionClick, canUpdate, canDelete]
  );

  if (isError) {
    return (
      <ErrorMessage
        message="Failed to load departments."
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <PageContainer className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-gray-500">College</span>
          <h1 className="text-2xl font-bold">Department Management</h1>
        </div>
        {canCreate && (
          <Button
            onClick={() =>
              deptDialog.open({ mode: "create", deptId: "", deptData: null })
            }
            className="flex items-center gap-2 bg-gray-900 text-white hover:bg-gray-800"
          >
            <Plus size={16} />
            Add New
          </Button>
        )}
      </div>

      {/* Search & Filter */}
      <div className="flex items-center gap-3">
        <Search
          placeholder="Search..."
          value={searchTerm}
          onValueChange={(val) => {
            setSearchTerm(val);
            resetPage();
          }}
          className={{ inputGroupClass: "max-w-xs" }}
        />

        <Select
          value={statusFilter}
          onValueChange={(val) => {
            setStatusFilter(val as typeof statusFilter);
            resetPage();
          }}
        >
          <SelectTrigger className="w-36 gap-2">
            <Filter size={14} className="text-gray-500" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-16">
              <Spinner />
            </div>
          ) : (
            <DataTable<DepartmentType, any>
              columns={columns}
              data={departments}
              // onRowClick={(dept) => navigate(`/department/${dept._id}`)}
            />
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <Pagination
        page={page}
        totalPages={totalPages}
        totalEntries={totalEntries}
        currentCount={departments.length}
        onPageChange={setPage}
      />

      {/* Create / Edit Dialog */}
      <DepartmentDialog
        mode={deptDialog.state.mode}
        isOpen={deptDialog.state.isOpen}
        onOpenChange={(open) => !open && deptDialog.close()}
        filters={filters}
        deptId={deptDialog.state.deptId}
        deptData={deptDialog.state.deptData}
        onCancel={deptDialog.close}
      />

      {/* Delete Confirmation */}
      <CustomAlertDialog
        isOpen={deleteDialog.state.isOpen}
        onOpenChange={(open) => !open && deleteDialog.close()}
        title="Confirm Deletion"
        description={deleteDialog.state.message}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={deleteDialog.close}
      />
    </PageContainer>
  );
};

export default DepartmentPage;
